import * as React from 'react';

import { faCheck, faFilterList, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Column, type ColumnFiltersState, type OnChangeFn } from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import DataTableMultiDropdown from 'src/registry/components/table/DataTableMultiDropdown';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { tableMessages } from 'src/registry/components/table/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { Input } from 'src/registry/ui/Input';
import { storage } from 'src/registry/utils/storage';
import { cn } from 'src/utils/shadcn';

import type { ColumnFilterShowDropdownState, DataTableColumnHeaderMeta } from 'src/registry/components/table/types';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'src/registry/ui/Command';
import * as _ from 'lodash';

type DataTableFilterProps<TData> = {
    setColumnFiltersShowDropdownState?: OnChangeFn<ColumnFilterShowDropdownState>;
    setColumnFiltersState?: OnChangeFn<ColumnFiltersState>;
    column: Column<TData>;
    open: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onFilterSearch?: (columnId: string, value: string) => void;
};

type DateRangeFilter = {
    afterDate: Date;
    beforeDate: Date;
};

export function DataTableFilter<TData>({
    setColumnFiltersShowDropdownState = () => {},
    setColumnFiltersState = () => {},
    column,
    open,
    onOpenChange,
    onFilterSearch,
}: DataTableFilterProps<TData>) {
    const { formatMessage } = useIntl();
    const [search, setSearch] = React.useState<string>('');

    const { badgeOptions, options, inputType, translatedInitialFilters, inlineDateFilter, dateFilter } = column
        .columnDef.meta as DataTableColumnHeaderMeta<TData, any>;
    const locale = storage.getLanguage();

    const formatDate = React.useCallback(
        (date: Date) =>
            date
                .toLocaleDateString(locale, {
                    year: 'numeric',
                    month: 'numeric',
                    day: '2-digit',
                })
                .replace('г.', ''),
        [locale],
    );

    const throttleHandler = React.useMemo(() => {
        if (!onFilterSearch) return null;

        return _.throttle((value: string) => {
            onFilterSearch(column.id, value);
        }, 300);
    }, [onFilterSearch, column.id]);

    React.useEffect(() => {
        if (!open || !throttleHandler) return;

        throttleHandler(search);
        return () => {
            throttleHandler.cancel();
        };
    }, [search, open, throttleHandler]);

    React.useEffect(() => {
        if (!open) {
            setSearch('');
        }
    }, [open]);

    const [filterLabel, setFilterLabel] = React.useState<string | undefined>(() => {
        const filterValue = column.getFilterValue();
        if (!filterValue) return formatMessage(tableMessages.noFilterApplied);

        if (typeof filterValue === 'string') {
            return translatedInitialFilters?.[filterValue] ?? filterValue;
        } else if (Array.isArray(filterValue)) {
            return filterValue.map((v) => translatedInitialFilters?.[v] ?? v).join(', ');
        } else if (
            filterValue &&
            typeof filterValue === 'object' &&
            !Array.isArray(filterValue) &&
            'afterDate' in filterValue &&
            'beforeDate' in filterValue
        ) {
            const { afterDate, beforeDate } = filterValue as DateRangeFilter;

            return `${formatDate(new Date(afterDate))} - ${formatDate(new Date(beforeDate))}`;
        }
        return formatMessage(tableMessages.noFilterApplied);
    });

    const [inFilterDropdownDateFilters, setInFilterDropdownDateFilters] = React.useState<DateRangeFilter>(() => {
        const afterDate = dateFilter?.afterDate ? new Date(dateFilter.afterDate) : new Date();
        const beforeDate = dateFilter?.beforeDate ? new Date(dateFilter.beforeDate) : new Date();

        afterDate.setHours(0, 0);
        beforeDate.setHours(23, 59);

        return {
            afterDate,
            beforeDate,
        };
    });

    const [inFilterDropdownSingleDateFilter, setInFilterDropdownSingleDateFilter] = React.useState<Date | undefined>(
        undefined,
    );

    React.useEffect(() => {
        const filterValue = column.getFilterValue?.() ?? column.getFilterValue();

        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
            setFilterLabel(undefined); // or "Няма приложени филтри"
            return;
        }

        if (typeof filterValue === 'string') {
            if (options) {
                const option = options.find((opt) => opt.value === filterValue);
                setFilterLabel(option?.label ?? filterValue);
            } else if (badgeOptions) {
                const badgeOption = badgeOptions[filterValue];
                setFilterLabel(formatMessage(badgeOption.label));
            } else {
                setFilterLabel(filterValue);
            }
        } else if (Array.isArray(filterValue)) {
            if (options) {
                setFilterLabel(
                    options
                        .filter((opt) => filterValue.includes(opt.value))
                        .map((opt) => opt.label)
                        .join(', '),
                );
            } else if (badgeOptions) {
                setFilterLabel(
                    filterValue
                        .map((val) => {
                            const badge = badgeOptions[val];
                            return badge ? formatMessage(badge.label) : val;
                        })
                        .join(', '),
                );
            } else {
                setFilterLabel(filterValue.join(', '));
            }
        } else if (
            filterValue &&
            typeof filterValue === 'object' &&
            !Array.isArray(filterValue) &&
            'afterDate' in filterValue &&
            'beforeDate' in filterValue
        ) {
            const { afterDate, beforeDate } = filterValue as DateRangeFilter;

            setInFilterDropdownDateFilters({
                afterDate: afterDate ?? dateFilter?.afterDate,
                beforeDate: beforeDate ?? dateFilter?.beforeDate,
            });
        } else if (typeof filterValue === 'object') {
            if (filterValue instanceof Date) {
                setFilterLabel(formatDate(filterValue));
                setInFilterDropdownSingleDateFilter(filterValue);
            }
        }
    }, [badgeOptions, column, formatMessage, column.getFilterValue(), options, setFilterLabel]);

    const onChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = String(e.target.value);
        column.setFilterValue(value);
        setFilterLabel(value);
        e.preventDefault();
    }, 500);

    React.useEffect(() => {
        if (!translatedInitialFilters) return;

        if (!filterLabel) {
            const filterValue = column.getFilterValue();
            if (typeof filterValue === 'string') {
                setFilterLabel(translatedInitialFilters[filterValue] ?? filterValue);
            } else if (Array.isArray(filterValue)) {
                setFilterLabel(filterValue.map((v) => translatedInitialFilters[v] ?? v).join(', '));
            }
        }
    }, [translatedInitialFilters]);

    const columnFilterLabel = (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)?.filterLabel;
    const customDropdownMenuLabel = (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)
        ?.customDropdownMenuLabel;

    const title =
        columnFilterLabel ??
        (typeof column.columnDef.header === 'string'
            ? column.columnDef.header
            : (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)?.title);

    const shouldRenderBody = React.useMemo(
        () => badgeOptions != null || options != null || inputType != null,
        [badgeOptions, options, inputType],
    );

    return (
        <DropdownMenu open={open} onOpenChange={onOpenChange}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="xsText" className="h-10 py-0">
                    <FontAwesomeIcon icon={faFilterList} className="text-primary-700 xs:block hidden" />
                    <span className="flex flex-col items-start sm:ml-1">
                        <span className="-mb-1 text-xs font-normal opacity-70">{title}:</span>
                        <span className="max-w-64 truncate text-sm">
                            {filterLabel ?? formatMessage(tableMessages.noFilterApplied)}
                        </span>
                    </span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                className={cn(
                    inputType !== INPUT_TYPE.SINGLE_DATE &&
                        inputType !== INPUT_TYPE.SEARCH &&
                        'w-full min-w-56 overflow-auto',
                )}
                onClick={(e) => e.stopPropagation()}
            >
                <DropdownMenuLabel>
                    {customDropdownMenuLabel
                        ? customDropdownMenuLabel
                        : formatMessage(tableMessages.filterColumnTitle, { column: title })}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {shouldRenderBody && inputType === INPUT_TYPE.SEARCH && (
                    <Command shouldFilter={false} className="w-full min-w-56 overflow-auto">
                        <CommandInput
                            placeholder={formatMessage(tableMessages.searchEntries, {
                                entries: title?.toLocaleLowerCase(),
                            })}
                            value={search}
                            onValueChange={(value) => {
                                setSearch(value);
                            }}
                            className="w-full"
                        />
                        <CommandList className="w-full">
                            <CommandEmpty>{formatMessage(tableMessages.noResults)}</CommandEmpty>
                            <CommandGroup>
                                {options?.map((option) => {
                                    const selectedValues = (column.getFilterValue() as string[]) ?? [];
                                    const isSelected = selectedValues.includes(option.value);

                                    return (
                                        <CommandItem
                                            key={option.value}
                                            value={option.value}
                                            onSelect={() => {
                                                let newValues: string[];
                                                if (isSelected) {
                                                    newValues = selectedValues.filter((v) => v !== option.value);
                                                } else {
                                                    newValues = [...selectedValues, option.value];
                                                }

                                                column.setFilterValue(newValues);

                                                const selectedLabels = options
                                                    .filter((opt) => newValues.includes(opt.value))
                                                    .map((opt) => opt.label);
                                                setFilterLabel(selectedLabels.join(', '));
                                            }}
                                            className={cn(
                                                'flex cursor-default items-center rounded-xs px-2 py-1.5 pl-8 text-sm break-all whitespace-normal outline-hidden select-none focus:bg-gray-100 data-[state=open]:bg-gray-100 dark:focus:bg-gray-800 dark:data-[state=open]:bg-gray-800',
                                            )}
                                        >
                                            <span className="absolute left-2 flex size-3.5 items-center justify-center">
                                                {isSelected && (
                                                    <FontAwesomeIcon icon={faCheck} className="text-primary-700" />
                                                )}
                                            </span>
                                            {option.label}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                )}
                {shouldRenderBody && inputType !== INPUT_TYPE.SEARCH && (
                    <>
                        <DropdownMenuRadioGroup
                            className={cn(inputType !== INPUT_TYPE.SINGLE_DATE && 'max-h-52 overflow-auto')}
                            value={column.getFilterValue() as string}
                            onValueChange={(value) => {
                                if (column.getFilterValue() === value) {
                                    column.setFilterValue('');
                                    setFilterLabel(undefined);
                                } else column.setFilterValue(value);
                            }}
                        >
                            {inputType === INPUT_TYPE.DATE && (
                                <DataTableDateDropdown
                                    setLabel={setFilterLabel}
                                    setValue={column.setFilterValue}
                                    afterDate={new Date(inFilterDropdownDateFilters.afterDate)}
                                    beforeDate={new Date(inFilterDropdownDateFilters.beforeDate)}
                                    enableTime={!inlineDateFilter}
                                />
                            )}
                            {inputType === INPUT_TYPE.SINGLE_DATE && (
                                <Calendar
                                    mode="single"
                                    onSelect={(date) => {
                                        if (!date) return;

                                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

                                        column.setFilterValue(utcDate);
                                        setFilterLabel(`${formatDate(utcDate)}`);
                                        setInFilterDropdownSingleDateFilter(utcDate);
                                    }}
                                    timeZone="UTC"
                                    selected={inFilterDropdownSingleDateFilter}
                                    defaultMonth={inFilterDropdownSingleDateFilter}
                                    required={false}
                                />
                            )}
                            {inputType === INPUT_TYPE.TEXT && <Input defaultValue={filterLabel} onChange={onChange} />}

                            {/*{inputType === INPUT_TYPE.SEARCH && (*/}
                            {/*    <Command shouldFilter={false} className='w-full'>*/}
                            {/*        <CommandInput*/}
                            {/*            placeholder={formatMessage(tableMessages.searchEntries, {*/}
                            {/*                entries: title?.toLocaleLowerCase(),*/}
                            {/*            })}*/}
                            {/*            value={search}*/}
                            {/*            onValueChange={(value) => {*/}
                            {/*                setSearch(value);*/}
                            {/*            }}*/}
                            {/*            className="w-full"*/}
                            {/*        />*/}

                            {/*        <CommandList className='w-full'>*/}
                            {/*            <CommandEmpty>{formatMessage(tableMessages.noResults)}</CommandEmpty>*/}

                            {/*            <CommandGroup>*/}
                            {/*                {options?.map((option) => (*/}
                            {/*                    <CommandItem*/}
                            {/*                        key={option.value}*/}
                            {/*                        value={option.value}*/}
                            {/*                        onSelect={() => {*/}
                            {/*                            column.setFilterValue(option.value);*/}
                            {/*                            setFilterLabel(option.label);*/}
                            {/*                        }}*/}
                            {/*                        className='whitespace-normal break-words'*/}
                            {/*                    >*/}
                            {/*                        {option.label}*/}
                            {/*                    </CommandItem>*/}
                            {/*                ))}*/}
                            {/*            </CommandGroup>*/}
                            {/*        </CommandList>*/}
                            {/*    </Command>*/}

                            {/*)}*/}

                            {options &&
                                (inputType === INPUT_TYPE.MULTI_SELECT ? (
                                    <DataTableMultiDropdown
                                        options={options}
                                        getFilterValue={column.getFilterValue as () => string[] | undefined}
                                        setFilterValue={column.setFilterValue}
                                    />
                                ) : (
                                    options.map((option) => (
                                        <DropdownMenuRadioItem
                                            onSelect={(e) => {
                                                setFilterLabel(option.label);
                                                e.preventDefault();
                                            }}
                                            value={option.value}
                                            key={option.value}
                                        >
                                            {option.label}
                                        </DropdownMenuRadioItem>
                                    ))
                                ))}
                            {badgeOptions &&
                                (inputType === INPUT_TYPE.MULTI_SELECT ? (
                                    <DataTableMultiDropdown
                                        badgeOptions={badgeOptions}
                                        getFilterValue={column.getFilterValue as () => string[] | undefined}
                                        setFilterValue={column.setFilterValue}
                                    />
                                ) : (
                                    Object.entries(badgeOptions).map(([key, value], i) => {
                                        const { iconColor, ...colors } = value.color ?? {};
                                        return (
                                            <DropdownMenuRadioItem
                                                onSelect={(e) => {
                                                    setFilterLabel(formatMessage(value.label));
                                                    e.preventDefault();
                                                }}
                                                value={key}
                                                key={`${title}-filter-${i}`}
                                            >
                                                <Badge theme={value.theme} className="gap-1.5" style={colors}>
                                                    {value.icon && (
                                                        <FontAwesomeIcon
                                                            icon={value.icon}
                                                            style={{ color: iconColor }}
                                                            size={value.iconSize}
                                                        />
                                                    )}
                                                    {formatMessage(value.label)}
                                                </Badge>
                                            </DropdownMenuRadioItem>
                                        );
                                    })
                                ))}
                        </DropdownMenuRadioGroup>
                        <DropdownMenuSeparator />
                    </>
                )}
                <DropdownMenuItemWithIcon
                    variant="destructive"
                    icon={faTrashAlt}
                    onSelect={() => {
                        setColumnFiltersShowDropdownState((prev) => prev.filter((id) => id !== column.id));
                        setFilterLabel(undefined);
                        // Keep the same array reference when this filter was never applied (nothing to remove).
                        // Returning a fresh array for a no-op clear still changes columnFiltersState, which
                        // triggers the table's "reset page / clear row selection" effect and closes any open
                        // side panel - even though the user never actually applied a filter
                        setColumnFiltersState((prev) => {
                            const next = prev.filter((filter) => filter.id !== column.id);
                            return next.length === prev.length ? prev : next;
                        });
                    }}
                >
                    {formatMessage(tableMessages.clearFilter)}
                </DropdownMenuItemWithIcon>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
